/**
 * The editor's form column — KANON `04-edytor.html`, top to bottom.
 *
 * Name, who holds it, the rhythm, the standard catalog, then the trainer's own questions and the
 * „Nowe pytanie" card. The order is the canon's, and the assigned bar sitting ABOVE the cadence is
 * part of it: who gets the form is the fact a trainer checks first, and a rhythm with nobody on it
 * is a setting with no consequence.
 *
 * ── A standard row is a LABEL, a mark, a type and ONE switch ─────────────────────────────────
 * The canon draws exactly that, and the screen had grown two more controls: a native orange
 * checkbox and a „Wymagane" text chip beside it. Both are gone (owner, 17 September 2026).
 *
 * The consequence is real and is recorded rather than hidden: **a catalog question's „required" is
 * no longer editable from this form.** It remains a fact about the question — drawn by the grey
 * lock beside its label, `RequiredMark` — and it is still what the catalog's own `defaultRequired`
 * and whatever the template already stored say it is; `toTemplateItems` keeps sending it. The
 * reducer's `setRequired` still accepts a standard key, so restoring a control is a rendering
 * change and nothing else. The canon draws no such control and the owner asked for the canon.
 *
 * ── Every refusal on this form is FOCUSABLE and says why, in the tree ────────────────────────
 * `aria-disabled` rather than the attribute, and the reason is a RENDERED element `aria-describedby`
 * points at — never `title` alone, which reaches neither a touch trainer nor a keyboard one who
 * never hovers (`shared/ui/dnd/DragHandle` states the rule; this form is its fifth consumer). The
 * intake's title and its four locked questions take the same shape; the locked switch carries its
 * own reason, inside `shared/ui/Toggle`.
 *
 * ── The recording is a ROW inside the last standard section, not a section of its own ────────
 * The screen drew „Nagranie ćwiczenia" as a whole bordered card with one dashed opener in it. The
 * canon does not: `05-edytor-dodaj-pytanie.html` puts one predefined row at the bottom of
 * „Trening i samopoczucie" with a switch on it, beside the weight and the sleep. So the card is
 * gone and the row is drawn where the export draws it, on the LAST standard section the catalog
 * hands over.
 *
 * Its switch is the only place on this form where a toggle adds or removes a question rather than
 * ticking one. On means „this survey asks for a recording": pressing it off takes the recording
 * questions out of the draft, pressing it on opens the exercise picker, because
 * `CustomQuestionRules` refuses a recording that names no movement. Both are staged like everything
 * else here — nothing is written until „Zapisz zmiany" — and both are announced.
 *
 * ── The standard section is grouped by the catalog's own sections ────────────────────────────
 * `SURVEY_SECTIONS` and its two words per section come from `model/surveyDetail`, which the Ankiety
 * card already uses. One grouping, two surfaces.
 */

import { useId, useRef, useState } from 'react';

import { useExerciseNames } from '~/entities/exercise-library';
import { ChevronRightIcon } from '~/shared/ui/AuthIcons';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ClientFace } from '../model/clientFace';
import {
  allowsClips,
  canAddClip,
  clipsLeft as clipsLeftOf,
  customItems,
  intakeRestrictions,
  offeredShelf,
  restrictionReason,
  shelfInsert,
  questionName,
  standardItems,
  type AuthoredQuestion,
  type CustomDraftItem,
  type EditorAction,
  type EditorDraft,
} from '../model/editorDraft';
import type { ShelfQuestion } from '../model/questionCatalog';
import { questionGlyph } from '../model/questionGlyphs';
import { SURVEY_SECTIONS, sectionOf, sectionSubtitle, sectionTitle } from '../model/surveyDetail';
import type { TranslateKey } from '../model/translateKey';
import { AnswerTypePill } from './AnswerTypePill';
import { AvatarStack } from './AvatarStack';
import { CadenceFields } from './CadenceFields';
import { CustomQuestionRow } from './CustomQuestionRow';
import { ExercisePicker } from './ExercisePicker';
import { ClientPicker } from './ClientPicker';
import { NewQuestionCard } from './NewQuestionCard';
import { QuestionGlyph } from './QuestionGlyph';
import { RequiredMark } from '~/shared/ui/RequiredMark';
import { Toggle } from '~/shared/ui/Toggle';
import { ChevronDownIcon, ClockIcon, SparkIcon, StarIcon } from '~/shared/ui/ReportIcons';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './EditorForm.module.css';

export interface EditorFormProps {
  readonly draft: EditorDraft;
  readonly dispatch: (action: EditorAction) => void;
  /**
   * Who holds the form, and the two writes that change it — a check-in's act only. Absent on a
   * host whose form is not assigned to clients (the public form builder), which draws no bar.
   */
  readonly assignment?: EditorAssignment | undefined;
  readonly shelf: readonly ShelfQuestion[];
  readonly onBookmark: (item: CustomDraftItem) => void;
  readonly bookmarkBusy: boolean;
  readonly announce: (message: string) => void;
  readonly onMove: (id: string, targetIndex: number, label: string) => void;
  readonly t: TranslateKey;
}

export interface EditorAssignment {
  readonly assignedIds: readonly number[];
  readonly faces: ReadonlyMap<number, ClientFace>;
  readonly onAssign: (customerId: number) => void;
  readonly onUnassign: (customerId: number) => void;
}

export function EditorForm({
  draft,
  dispatch,
  assignment,
  shelf,
  onBookmark,
  bookmarkBusy,
  announce,
  onMove,
  t,
}: EditorFormProps) {
  const nameId = useId();
  const nameReasonId = useId();
  const hintId = useId();
  const [adding, setAdding] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  /** The question waiting on a movement: written, not yet added. `null` when the picker is closed. */
  const [awaitingExercise, setAwaitingExercise] = useState<AuthoredQuestion | null>(null);
  const manageRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);

  const restrictions = intakeRestrictions(draft);
  const standards = standardItems(draft);
  const customs = customItems(draft);

  // One batch for every recording question on the form, not one read per row.
  const clipIds = customs.flatMap((item) => (item.exerciseId === null ? [] : [item.exerciseId]));
  const exerciseNames = useExerciseNames(clipIds);

  const clips = customs.filter((item) => item.answerType === 'ExerciseClip');
  const clipName =
    clips[0]?.exerciseId === undefined || clips[0].exerciseId === null
      ? null
      : (exerciseNames.names.get(clips[0].exerciseId) ?? null);

  const addQuestion = (question: AuthoredQuestion): void => {
    dispatch({ type: 'addCustom', id: crypto.randomUUID(), question });
    setAdding(false);
    setAwaitingExercise(null);
    announce(t('web:reports.editor.dnd.added', { label: question.label }));
  };

  /** Which standard sections the catalog actually filled, in the canon's order. */
  const drawnSections = SURVEY_SECTIONS.filter((section) =>
    standards.some((item) => sectionOf(item.section) === section),
  );
  const clipSection = drawnSections.at(-1) ?? null;

  const openClipPicker = (): void => {
    setAwaitingExercise({
      label: '',
      answerType: 'ExerciseClip',
      options: [],
      required: false,
      exerciseId: null,
      sourceQuestionId: null,
    });
  };

  const dropClips = (): void => {
    for (const clip of clips) {
      dispatch({ type: 'removeCustom', id: clip.id });
      announce(t('web:reports.editor.dnd.removed', { label: clip.label }));
    }
  };

  // A FRAGMENT, not a box of its own: the column's width, its gap and its reserve belong to
  // `.form` on the screen, which also carries the gate's hand-over and the write banner. Two
  // nested `.form` elements were two 640px columns and two 560px reserves stacked on one another.
  return (
    <>
      {/* A lead form has no name of its own to edit — its PATCH takes items only, and the public
        * form it belongs to is what the trainer names (public-form R4). */}
      {draft.kind === 'leadForm' ? null : (
        <div className={styles.nameBlock}>
          <span className={canon.eyebrow}>
            {draft.kind === 'intake'
              ? t('web:reports.surveys.detail.intakeEyebrow')
              : t('web:reports.surveys.detail.checkInEyebrow')}
          </span>
          <label className={styles.label} htmlFor={nameId}>
            {t('web:reports.editor.name')}
          </label>
          <input
            id={nameId}
            className={styles.nameField}
            value={draft.title}
            readOnly={restrictions.titleLocked}
            aria-describedby={restrictions.titleLocked ? nameReasonId : undefined}
            title={restrictions.titleLocked ? restrictionReason('title', t) : undefined}
            onChange={(event) => {
              dispatch({ type: 'setTitle', title: event.target.value });
            }}
          />
          <p className={styles.note} id={restrictions.titleLocked ? nameReasonId : undefined}>
            {restrictions.titleLocked ? restrictionReason('title', t) : t('web:reports.editor.nameHint')}
          </p>
        </div>
      )}

      {/* A form that does not exist yet has nobody on it, and nothing to manage. Who may hold the
        * form is the model's (`intakeRestrictions.assignment`): the intake has everybody and nothing
        * to manage, a lead form nobody at all. */}
      {draft.templateId === null || assignment === undefined || restrictions.assignment === 'nobody' ? null : restrictions.assignment === 'everyone' ? (
        <div className={styles.assigned}>
          <span className={styles.assignedNote}>{t('web:reports.editor.intakeAssigned')}</span>
        </div>
      ) : (
        <div className={styles.assigned}>
          {/* The editor's stack is the PHONE's — 32px discs, nine of overlap, five before it counts
            * (owner, 17 September 2026). The 34/−6/4 stack is the LIST's, on a 392px card. One
            * component, two stated sizes. */}
          <AvatarStack
            customerIds={assignment.assignedIds}
            count={assignment.assignedIds.length}
            faces={assignment.faces}
            size="editor"
          />
          <span className={styles.assignedCount}>
            {t('web:reports.editor.assigned', { count: assignment.assignedIds.length })}
          </span>
          <button
            ref={manageRef}
            type="button"
            className={styles.manage}
            onClick={() => {
              setClientsOpen(true);
            }}
          >
            {t('web:reports.editor.manage')}
            <ChevronRightIcon className={styles.manageIcon} />
          </button>
        </div>
      )}

      {draft.cadence === null ? null : (
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={canon.tile}>
              <ClockIcon className={canon.tileIcon} />
            </span>
            <span className={styles.sectionText}>
              <h2 className={styles.sectionTitle}>{t('web:reports.editor.cadence.title')}</h2>
              <span className={styles.sectionHint}>{t('web:reports.editor.cadence.hint')}</span>
            </span>
            <ChevronDownIcon className={styles.sectionChevron} />
          </div>
          <div className={styles.sectionBody}>
            <CadenceFields
              cadence={draft.cadence}
              onChange={(patch) => {
                dispatch({ type: 'setCadence', cadence: patch });
              }}
              t={t}
            />
          </div>
        </section>
      )}

      {drawnSections.map((section) => {
        // `sectionOf`, never a local `?? 'additional'`: that fallback routes `null` correctly and
        // DROPS a section name the catalog has since added, so the question would vanish from the
        // editor while the Ankiety card — which does go through the model — still lists it.
        const rows = standards.filter((item) => sectionOf(item.section) === section);
        const on = rows.filter((item) => item.included).length;
        return (
          <section key={section} className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={canon.tile}>
                <StarIcon className={canon.tileIcon} />
              </span>
              <span className={styles.sectionText}>
                <h2 className={styles.sectionTitle}>{sectionTitle(section, t)}</h2>
                <span className={styles.sectionHint}>{sectionSubtitle(section, t)}</span>
              </span>
              <span className={styles.sectionCount}>
                {t('web:reports.editor.sectionCount', { on, total: rows.length })}
              </span>
              <ChevronDownIcon className={styles.sectionChevron} />
            </div>
            <ul className={styles.sectionBody}>
              {rows.map((item) => {
                // The catalog's label is English by design; `questionName` reads the code. See
                // `model/editorDraft`.
                const name = questionName(item, t);
                const reason = item.locked ? restrictionReason('locked', t) : null;
                return (
                  <li key={item.code} className={styles.row}>
                    <span className={styles.rowText}>
                      <span
                        className={`${String(styles.rowLabel)} ${
                          item.included ? '' : String(styles.rowOff)
                        }`}
                      >
                        {/* One mark per predefined question, BEFORE the word (owner, 17 September
                          * 2026). `model/questionGlyphs` decides which; the row states the ink. */}
                        <QuestionGlyph
                          glyph={questionGlyph(item.code, item.answerType, item.vitalType)}
                          className={styles.rowGlyph}
                        />
                        <span className={styles.rowLabelText}>{name}</span>
                        {item.required ? (
                          <RequiredMark label={t('web:reports.editor.newQuestion.required')} />
                        ) : null}
                      </span>
                      <span className={styles.rowMeta}>
                        <AnswerTypePill answerType={item.answerType} t={t} />
                        {item.mapTarget === 'InitialWeight' ||
                        item.mapTarget === 'BodyMeasurement' ||
                        item.mapTarget === 'CompositeMeasurement' ? (
                          <span className={`${String(styles.typePill)} ${String(styles.mapNoteInk)}`}>
                            {t('web:reports.surveys.detail.mapBody')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <Toggle
                      checked={item.included}
                      label={t('web:reports.editor.include', { label: name })}
                      lockedReason={reason}
                      onChange={(included) => {
                        dispatch({ type: 'includeStandard', code: item.code, included });
                      }}
                    />
                  </li>
                );
              })}

              {/* The recording, as the canon draws it: the last row of the last standard section.
                * Its switch is the one on this form that adds and removes rather than ticks — the
                * header says why, and the row appears on a check-in only, because the intake is
                * filled once and has no movement to film against a cycle. */}
              {section === clipSection && draft.cadence !== null ? (
                <li className={styles.row}>
                  <span className={styles.rowText}>
                    <span
                      className={`${String(styles.rowLabel)} ${
                        clips.length > 0 ? '' : String(styles.rowOff)
                      }`}
                    >
                      {/* This row is PREDEFINED too — the canon draws it among the standard ones —
                        * so it carries a mark like its neighbours. It has no catalog code to key on
                        * (it is the recording pseudo-row), so the key is named: the clip. */}
                      <QuestionGlyph glyph="clip" className={styles.rowGlyph} />
                      <span className={styles.rowLabelText}>{t('web:reports.editor.clip.title')}</span>
                    </span>
                    <span className={styles.rowMeta}>
                      <AnswerTypePill answerType="ExerciseClip" exerciseName={clipName} t={t} />
                      {clips.length === 0 ? (
                        <span className={styles.typePill}>
                          {t('web:reports.editor.clip.hint', { left: clipsLeftOf(draft) })}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <Toggle
                    checked={clips.length > 0}
                    label={t('web:reports.editor.clip.toggle')}
                    lockedReason={
                      clips.length === 0 && !canAddClip(draft)
                        ? t('web:reports.editor.newQuestion.clipCapReached')
                        : null
                    }
                    onChange={(wanted) => {
                      if (wanted) openClipPicker();
                      else dropClips();
                    }}
                  />
                </li>
              ) : null}
            </ul>
          </section>
        );
      })}

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <span className={canon.tile}>
            <SparkIcon className={canon.tileIcon} />
          </span>
          <span className={styles.sectionText}>
            <h2 className={styles.sectionTitle}>{t('web:reports.surveys.section.additional.title')}</h2>
            <span className={styles.sectionHint}>{t('web:reports.editor.custom.hint')}</span>
          </span>
          <span className={styles.sectionCount}>{customs.length}</span>
          <ChevronDownIcon className={styles.sectionChevron} />
        </div>
        <ul className={styles.sectionBody}>
          {customs.map((item, index) => (
            <CustomQuestionRow
              key={item.id}
              item={item}
              index={index}
              count={customs.length}
              exerciseName={
                item.exerciseId === null ? null : (exerciseNames.names.get(item.exerciseId) ?? null)
              }
              hintId={hintId}
              onMove={onMove}
              onLift={(position) => {
                // A pickup and an arrow press must not sound the same: the lift names what is being
                // carried and what the arrows now do, which `useKeyboardMove`'s own header requires.
                announce(
                  t('web:reports.editor.dnd.lifted', {
                    label: item.label,
                    position: position + 1,
                    count: customs.length,
                  }),
                );
              }}
              onPreview={(position) => {
                announce(
                  t('web:reports.editor.dnd.position', {
                    position: position + 1,
                    count: customs.length,
                  }),
                );
              }}
              onCancelLift={() => {
                announce(t('web:reports.editor.dnd.cancelled', { label: item.label }));
              }}
              onRemove={(id) => {
                dispatch({ type: 'removeCustom', id });
                announce(t('web:reports.editor.dnd.removed', { label: item.label }));
              }}
              onBookmark={onBookmark}
              bookmarkBusy={bookmarkBusy}
              t={t}
            />
          ))}
        </ul>

        <span className={hidden.srOnly} id={hintId}>
          {t('web:reports.editor.dnd.hint')}
        </span>

        <div className={styles.addRow}>
          {adding ? (
            <NewQuestionCard
              shelf={offeredShelf(draft, shelf)}
              allowClip={allowsClips(draft)}
              clipsLeft={clipsLeftOf(draft)}
              onAdd={addQuestion}
              onPickExercise={setAwaitingExercise}
              onPickShelf={(question) => {
                addQuestion(shelfInsert(question));
              }}
              onCancel={() => {
                setAdding(false);
                addRef.current?.focus();
              }}
              t={t}
            />
          ) : (
            <button
              ref={addRef}
              type="button"
              className={canon.dash}
              onClick={() => {
                setAdding(true);
              }}
            >
              <ScreenHeaderPlus className={canon.dashIcon} />
              {t('web:reports.editor.custom.add')}
            </button>
          )}
        </div>
      </section>

      {assignment === undefined || restrictions.assignment !== 'clients' ? null : (
        <ClientPicker
          open={clientsOpen}
          assignedIds={assignment.assignedIds}
          faces={assignment.faces}
          onAssign={assignment.onAssign}
          onUnassign={assignment.onUnassign}
          onClose={() => {
            setClientsOpen(false);
          }}
          triggerRef={manageRef}
          t={t}
        />
      )}

      <ExercisePicker
        pending={awaitingExercise}
        onChoose={(exerciseId, name) => {
          const question = awaitingExercise;
          if (question === null) return;
          addQuestion({
            ...question,
            // A recording written from the card keeps the trainer's own wording; one added from the
            // predefined row has none yet, so the movement names it.
            label: question.label.trim() === '' ? t('web:reports.editor.clip.label', { name }) : question.label,
            exerciseId,
          });
        }}
        onClose={() => {
          setAwaitingExercise(null);
        }}
        triggerRef={addRef}
        t={t}
      />
    </>
  );
}
